import { useEffect, useState } from 'react'
import { KpiCard } from '@/components/ui/KpiCard'
import { Section, GrowthBadge, LineChart, RecentList } from '@/pages/Dashboard'
import { PartnerService, type PartnerDashboard } from '@/services/modules/PartnerService'
import { brlFromCents } from '@/lib/format'

const TYPE_LABELS: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  FAMILIAR: 'Familiar',
  EMPRESARIAL: 'Empresarial',
}

/**
 * Dashboard do parceiro (diretor/supervisor/afiliado/vendedor).
 * Centrado nas COMISSÕES dele + adesões da rede — sem faturamento das empresas.
 */
export function PartnerDashboardPage() {
  const [data, setData] = useState<PartnerDashboard | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    PartnerService.dashboard()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500">Suas comissões e a evolução da sua rede de vendas.</p>
      </div>

      {loading || !data ? (
        <div className="text-sm text-slate-500">{loading ? 'Carregando' : 'Não foi possível carregar o painel.'}</div>
      ) : (
        <>
          {/* 01 — Comissões recebidas (últimos 3 anos) */}
          <Section title="Comissões recebidas" subtitle="Total recebido em comissões nos últimos 3 anos">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {data.yearlyCommissions.map((y) => (
                <KpiCard
                  key={y.year}
                  label={String(y.year)}
                  value={brlFromCents(y.total)}
                  hint={y.growthPct === null ? undefined : <GrowthBadge pct={y.growthPct} />}
                />
              ))}
            </div>
          </Section>

          {/* 02 — Mês corrente: recebidas, pendentes, a pagar + adesões */}
          <Section title="Mês corrente" subtitle="Suas comissões e adesões da rede no mês atual">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                label="Comissões recebidas"
                accent="green"
                value={brlFromCents(data.currentMonth.commissionsReceived.amount)}
                hint={`${data.currentMonth.commissionsReceived.count} · faturas pagas`}
              />
              <KpiCard
                label="Comissões pendentes"
                accent="amber"
                value={brlFromCents(data.currentMonth.commissionsPending.amount)}
                hint={`${data.currentMonth.commissionsPending.count} · faturas não pagas`}
              />
              <KpiCard
                label="Comissões a pagar (sua rede)"
                accent="rose"
                value={brlFromCents(data.currentMonth.commissionsToPay.amount)}
                hint={`${data.currentMonth.commissionsToPay.count} comissão(ões)`}
              />
              <KpiCard
                label="Adesões no mês"
                accent="brand"
                value={data.currentMonth.adhesions.count}
                hint="Toda a sua rede (incl. você)"
              />
            </div>
          </Section>

          {/* 03 — Adesões mensais da rede + assinaturas ativas */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-1 font-medium text-slate-900">Adesões mensais</h3>
              <p className="mb-4 text-xs text-slate-500">Titulares da sua rede — últimos 6 meses</p>
              <LineChart data={data.monthlyAdhesions.map((m) => ({ label: m.label, value: m.count }))} />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-1 font-medium text-slate-900">Assinaturas ativas</h3>
              <p className="mb-4 text-xs text-slate-500">{data.activeSubscriptions.total} no total na sua rede</p>
              <div className="grid grid-cols-2 gap-3">
                <KpiCard label="Total ativas" accent="brand" value={data.activeSubscriptions.total} />
                {data.activeSubscriptions.byType.map((t) => (
                  <KpiCard key={t.type} label={TYPE_LABELS[t.type] ?? t.type} value={t.total} />
                ))}
              </div>
            </div>
          </div>

          {/* 04 — Últimas assinaturas da rede (somente leitura) */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-4 font-medium text-slate-900">Últimas assinaturas da sua rede</h3>
            <RecentList
              rows={data.latestSubscribers.map((s) => ({
                id: s.id,
                primary: s.name,
                secondary: s.planName ?? '—',
                meta: s.formatted.createdAt ?? '',
                status: s.status,
              }))}
              empty="Nenhuma assinatura na sua rede ainda."
            />
          </div>
        </>
      )}
    </div>
  )
}

export default PartnerDashboardPage
